/* globals DOMParser */

/** Pull a human-readable message out of a js-sdk APIError (or plain Error). */
export const readAPIErrorMessage = (err: unknown): string => {
  const body = (err as { body?: { message?: string; error?: string } }).body;
  return (
    body?.message ||
    body?.error ||
    (err as Error).message ||
    'Something went wrong'
  );
};

type Ring = [number, number][];

/**
 * Parse a KML `<coordinates>` text block: whitespace-separated
 * `lon,lat[,alt]` tuples → a [lon, lat] ring, closed if needed.
 */
const parseCoordinatesText = (text: string): Ring => {
  const ring: Ring = [];
  for (const tuple of text.trim().split(/\s+/)) {
    const parts = tuple.split(',');
    if (parts.length < 2) continue;
    const lon = Number(parts[0]);
    const lat = Number(parts[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    ring.push([lon, lat]);
  }
  // GeoJSON rings must be closed (first position === last position)
  if (ring.length >= 3) {
    const [firstLon, firstLat] = ring[0];
    const [lastLon, lastLat] = ring[ring.length - 1];
    if (firstLon !== lastLon || firstLat !== lastLat) {
      ring.push([firstLon, firstLat]);
    }
  }
  return ring;
};

/** Ring for one boundary element (outerBoundaryIs / innerBoundaryIs). */
const ringFromBoundary = (boundary: Element): Ring | null => {
  const coordinates = boundary.getElementsByTagName('coordinates')[0];
  if (!coordinates?.textContent) return null;
  const ring = parseCoordinatesText(coordinates.textContent);
  // A valid closed ring has at least 4 positions
  return ring.length >= 4 ? ring : null;
};

/**
 * Convert KML text into a GeoJSON Polygon/MultiPolygon boundary in the
 * browser (the map-site API only accepts GeoJSON). Handles multiple
 * `<Placemark>` polygons (→ MultiPolygon), `outerBoundaryIs` /
 * `innerBoundaryIs` rings, and ring closure. Throws with a user-facing
 * message when no polygon can be extracted.
 */
export const kmlToBoundaryGeoJSON = (
  kmlText: string,
): Record<string, unknown> => {
  const doc = new DOMParser().parseFromString(kmlText, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('That file is not valid KML');
  }

  const polygons: Ring[][] = [];
  // getElementsByTagName matches unprefixed KML tags regardless of the
  // default namespace declaration
  for (const polygon of Array.from(doc.getElementsByTagName('Polygon'))) {
    const outerBoundary =
      polygon.getElementsByTagName('outerBoundaryIs')[0] ?? polygon;
    const outerRing = ringFromBoundary(outerBoundary);
    if (!outerRing) continue;
    const rings: Ring[] = [outerRing];
    for (const innerBoundary of Array.from(
      polygon.getElementsByTagName('innerBoundaryIs'),
    )) {
      const innerRing = ringFromBoundary(innerBoundary);
      if (innerRing) rings.push(innerRing);
    }
    polygons.push(rings);
  }

  if (polygons.length === 0) {
    throw new Error(
      'No polygon found in the KML file — the boundary must contain at least one polygon.',
    );
  }
  if (polygons.length === 1) {
    return { type: 'Polygon', coordinates: polygons[0] };
  }
  return { type: 'MultiPolygon', coordinates: polygons };
};
